<template>
  <v-card
    :elevation="variant === 'card' ? 2 : 0"
    class="community-group-chat"
    :class="{
      'community-group-chat--card': variant === 'card',
      'community-group-chat--embedded': variant === 'tab',
      'community-group-chat--overlay': variant === 'overlay',
    }"
  >
    <div v-if="!hideHeader" class="community-group-chat__header">
      <div class="text-subtitle-2">Chat del grupo</div>
      <div class="text-caption text-medium-emphasis">
        *negrita* · _cursiva_ · ~tachado~
      </div>
    </div>

    <div class="community-group-chat__thread">
      <div ref="messagesEl" class="community-group-chat__messages" @scroll="onMessagesScroll">
      <div v-if="loadingInitial" class="d-flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" size="28" width="2" />
      </div>

      <v-alert
        v-else-if="loadError"
        type="error"
        variant="tonal"
        density="compact"
        class="ma-3"
      >
        {{ loadError }}
      </v-alert>

      <div
        v-else-if="messages.length === 0"
        class="community-group-chat__empty text-body-2 text-medium-emphasis"
      >
        Todavía no hay mensajes. Sé el primero en escribir.
      </div>

      <div
        v-for="message in messages"
        :key="message.messageId"
        :data-message-id="message.messageId"
        class="community-group-chat__message"
        :class="{ 'community-group-chat__message--own': message.authorId === currentUserId }"
      >
        <AvatarAsset
          :signed-url="message.avatarSignedUrl"
          :size="32"
          :seed="message.authorId"
          class="community-group-chat__avatar"
        />
        <div class="community-group-chat__bubble">
          <div class="community-group-chat__meta">
            <span class="community-group-chat__alias">{{ message.publicAlias || 'Escritor' }}</span>
            <span class="community-group-chat__time">{{ formatTime(message.createdAt) }}</span>
          </div>
          <div
            class="community-group-chat__content"
            v-html="formatContent(message.content)"
          />
        </div>
      </div>
      </div>
      <div v-if="showJumpToUnread" class="community-group-chat__jump">
        <v-btn
          size="small"
          color="primary"
          variant="flat"
          prepend-icon="mdi-chevron-up"
          :loading="jumpLoading"
          aria-label="Ir al primer mensaje sin leer"
          @click="jumpToFirstUnread"
        >
          Mensajes sin leer
        </v-btn>
      </div>
    </div>

    <div class="community-group-chat__composer">
      <v-alert
        v-if="rateLimitSecondsLeft > 0"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-2"
      >
        Para evitar spam, espera {{ rateLimitSecondsLeft }}s antes de enviar otro mensaje.
      </v-alert>
      <v-alert
        v-else-if="sendError"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-2"
      >
        {{ sendError }}
      </v-alert>
      <div class="community-group-chat__composer-row">
        <v-text-field
          v-model="draft"
          variant="outlined"
          density="compact"
          hide-details
          maxlength="1000"
          placeholder="Escribe un mensaje"
          :disabled="sending || rateLimitSecondsLeft > 0"
          @keydown.enter.prevent="send"
        />
        <v-btn
          color="primary"
          variant="flat"
          icon
          :loading="sending"
          :disabled="!canSend"
          aria-label="Enviar mensaje"
          @click="send"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue'
import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'

import AvatarAsset from '@/components/assets/AvatarAsset.vue'
import GroupsService from '@/services/groups'
import { useAuthStore } from '@/stores/auth'
import { useGroupChatsStore } from '@/stores/groupChats'
import type { GroupChatHead, GroupChatMessage } from '@/types/groups'
import { formatGroupChatContent } from '@/utils/formatGroupChatContent'

const POLL_INTERVAL_MS = 3000
const MARK_READ_DELAY_MS = 1000
const JUMP_VISIBLE_MS = 5000
const NEAR_BOTTOM_PX = 48
const HISTORY_PAGE_LIMIT = 50
const MAX_HISTORY_PAGES = 20
const GROUP_CHAT_RATE_LIMIT_ERROR_CODE = 'GROUP_CHAT_RATE_LIMIT'

export default defineComponent({
  name: 'CommunityGroupChatPanel',

  components: {
    AvatarAsset,
  },

  props: {
    groupId: {
      type: String,
      required: true,
    },
    variant: {
      type: String as PropType<'card' | 'tab' | 'overlay'>,
      default: 'card',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const authStore = useAuthStore()
    const groupChatsStore = useGroupChatsStore()
    const messages = ref<GroupChatMessage[]>([])
    const draft = ref('')
    const loadingInitial = ref(true)
    const sending = ref(false)
    const loadError = ref('')
    const sendError = ref('')
    const rateLimitSecondsLeft = ref(0)
    const messagesEl = ref<HTMLElement | null>(null)
    const knownHeadId = ref<string | null>(null)
    const lastReadMessageId = ref<string | null>(null)
    const unreadCount = ref(0)
    const showJumpToUnread = ref(false)
    const jumpLoading = ref(false)
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let rateLimitTimer: ReturnType<typeof setInterval> | null = null
    let markTimer: ReturnType<typeof setTimeout> | null = null
    let jumpTimer: ReturnType<typeof setTimeout> | null = null
    let jumpTargetId: string | null = null
    let jumpReadCursor: string | null = null
    let jumpCycleDismissed = false
    let historyExhausted = false
    let marking = false
    let disposed = false

    const currentUserId = computed(() => authStore.currentUser?.userId || '')
    const canSend = computed(() => (
      draft.value.trim().length > 0
      && !sending.value
      && rateLimitSecondsLeft.value <= 0
    ))

    const formatContent = (content: string) => formatGroupChatContent(content)

    const formatTime = (iso: string) => {
      const date = parseISO(iso)
      if (isToday(date)) {
        return format(date, 'HH:mm')
      }
      if (isYesterday(date)) {
        return `Ayer ${format(date, 'HH:mm')}`
      }
      return format(date, 'd MMM HH:mm', { locale: es })
    }

    const scrollToBottom = async () => {
      await nextTick()
      const el = messagesEl.value
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    }

    const isNearBottom = () => {
      const el = messagesEl.value
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
    }

    const findFirstUnread = () => {
      const readId = lastReadMessageId.value
      const userId = currentUserId.value
      return messages.value.find((message) => {
        if (message.authorId === userId) return false
        if (!readId) return true
        return message.messageId > readId
      }) || null
    }

    const coversReadCursor = () => {
      if (historyExhausted) return true
      const oldest = messages.value[0]
      if (!oldest) return true
      if (!lastReadMessageId.value) return false
      return oldest.messageId <= lastReadMessageId.value
    }

    const isAboveView = (messageId: string) => {
      const container = messagesEl.value
      if (!container) return false
      const node = container.querySelector(`[data-message-id="${messageId}"]`)
      if (!node) return true
      return node.getBoundingClientRect().top < container.getBoundingClientRect().top - 4
    }

    const isLastMessageVisible = () => {
      const container = messagesEl.value
      const last = messages.value[messages.value.length - 1]
      if (!container || !last) return false
      const node = container.querySelector(`[data-message-id="${last.messageId}"]`)
      if (!node) return false
      const containerRect = container.getBoundingClientRect()
      const nodeRect = node.getBoundingClientRect()
      return nodeRect.bottom > containerRect.top && nodeRect.top < containerRect.bottom
    }

    const hasUnreadOutsideView = () => {
      if (loadingInitial.value || unreadCount.value <= 0) return false
      if (!coversReadCursor()) return true
      const first = findFirstUnread()
      if (!first) return false
      return isAboveView(first.messageId)
    }

    const clearJumpTimer = () => {
      if (jumpTimer) {
        clearTimeout(jumpTimer)
        jumpTimer = null
      }
    }

    const resetJumpCycle = () => {
      clearJumpTimer()
      showJumpToUnread.value = false
      jumpCycleDismissed = false
      jumpTargetId = null
      jumpReadCursor = null
    }

    const startJumpCycle = () => {
      const first = findFirstUnread()
      jumpTargetId = first?.messageId || null
      jumpReadCursor = lastReadMessageId.value
      showJumpToUnread.value = true
      clearJumpTimer()
      jumpTimer = setTimeout(() => {
        jumpTimer = null
        showJumpToUnread.value = false
        jumpCycleDismissed = true
      }, JUMP_VISIBLE_MS)
    }

    const updateJumpVisibility = () => {
      if (loadingInitial.value) return
      const outside = hasUnreadOutsideView()
      if (!outside) {
        jumpCycleDismissed = false
      }
      if (showJumpToUnread.value || !outside || jumpCycleDismissed) return
      startJumpCycle()
    }

    const applyReadState = (head: GroupChatHead) => {
      const currentRead = lastReadMessageId.value
      const incomingRead = head.lastReadMessageId
      if (!(currentRead && (!incomingRead || incomingRead < currentRead))) {
        lastReadMessageId.value = incomingRead
        unreadCount.value = head.unreadCount
      }
      groupChatsStore.applyChatHead(props.groupId, {
        ...head,
        lastReadMessageId: lastReadMessageId.value,
        unreadCount: unreadCount.value,
      })
    }

    const clearMarkTimer = () => {
      if (markTimer) {
        clearTimeout(markTimer)
        markTimer = null
      }
    }

    const canMarkRead = () => {
      if (disposed || document.hidden || !document.hasFocus()) return false
      const last = messages.value[messages.value.length - 1]
      if (!last || last.messageId !== knownHeadId.value) return false
      if (lastReadMessageId.value && lastReadMessageId.value >= last.messageId) return false
      return isLastMessageVisible()
    }

    const markReadNow = async () => {
      if (marking || disposed) return
      const target = messages.value[messages.value.length - 1]
      if (!target || target.messageId !== knownHeadId.value) return
      if (lastReadMessageId.value && lastReadMessageId.value >= target.messageId) return
      marking = true
      try {
        const response = await GroupsService.markChatRead(props.groupId, target.messageId)
        if (disposed) return
        applyReadState(response.data)
        updateJumpVisibility()
      } catch {
        // A failed read cursor can be retried on the next focus or scroll.
      } finally {
        marking = false
      }
    }

    const scheduleMarkRead = () => {
      if (!canMarkRead()) {
        clearMarkTimer()
        return
      }
      if (markTimer) return
      markTimer = setTimeout(() => {
        markTimer = null
        if (!canMarkRead()) return
        void markReadNow()
      }, MARK_READ_DELAY_MS)
    }

    const scrollToMessage = (messageId: string) => {
      const container = messagesEl.value
      if (!container) return
      const node = container.querySelector(`[data-message-id="${messageId}"]`)
      if (!node) return
      const delta = node.getBoundingClientRect().top - container.getBoundingClientRect().top
      container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' })
    }

    const prependMessages = async (incoming: GroupChatMessage[]) => {
      const el = messagesEl.value
      const prevHeight = el?.scrollHeight ?? 0
      const prevTop = el?.scrollTop ?? 0
      mergeMessages(incoming)
      await nextTick()
      if (el) {
        el.scrollTop = el.scrollHeight - prevHeight + prevTop
      }
    }

    const mergeMessages = (incoming: GroupChatMessage[]) => {
      if (incoming.length === 0) return
      const byId = new Map(messages.value.map((message) => [message.messageId, message]))
      incoming.forEach((message) => {
        byId.set(message.messageId, message)
      })
      messages.value = [...byId.values()].sort((a, b) => (
        a.messageId < b.messageId ? -1 : a.messageId > b.messageId ? 1 : 0
      ))
    }

    const clearRateLimitTimer = () => {
      if (rateLimitTimer) {
        clearInterval(rateLimitTimer)
        rateLimitTimer = null
      }
    }

    const startRateLimitCountdown = (seconds: number) => {
      clearRateLimitTimer()
      rateLimitSecondsLeft.value = Math.max(1, seconds)
      rateLimitTimer = setInterval(() => {
        rateLimitSecondsLeft.value = Math.max(0, rateLimitSecondsLeft.value - 1)
        if (rateLimitSecondsLeft.value <= 0) {
          clearRateLimitTimer()
        }
      }, 1000)
    }

    const extractRetryAfterSeconds = (error: any): number | null => {
      const data = error?.response?.data
      const seconds =
        data?.retryAfterSeconds
        || data?.data?.retryAfterSeconds
        || null
      return typeof seconds === 'number' && seconds > 0 ? seconds : null
    }

    const ensureLoadedBackTo = async (messageId: string | null) => {
      for (let page = 0; page < MAX_HISTORY_PAGES; page += 1) {
        if (disposed) return
        if (messageId && messages.value.some((message) => message.messageId === messageId)) return
        const oldest = messages.value[0]
        if (!oldest) return
        if (messageId && oldest.messageId <= messageId) return
        const response = await GroupsService.listMessages(props.groupId, {
          beforeId: oldest.messageId,
          limit: HISTORY_PAGE_LIMIT,
        })
        if (disposed) return
        if (response.data.length > 0) {
          await prependMessages(response.data)
        }
        if (response.data.length < HISTORY_PAGE_LIMIT) {
          historyExhausted = true
          return
        }
      }
    }

    const jumpToFirstUnread = async () => {
      if (jumpLoading.value) return
      clearMarkTimer()
      jumpLoading.value = true
      const targetId = jumpTargetId
      const readCursor = jumpReadCursor
      try {
        if (targetId) {
          await ensureLoadedBackTo(targetId)
          await nextTick()
          scrollToMessage(targetId)
          return
        }
        await ensureLoadedBackTo(readCursor)
        await nextTick()
        const target = messages.value.find((message) => {
          if (message.authorId === currentUserId.value) return false
          if (!readCursor) return true
          return message.messageId > readCursor
        })
        if (target) {
          scrollToMessage(target.messageId)
        }
      } catch {
        // Leave the button in place so the user can retry.
      } finally {
        jumpLoading.value = false
      }
    }

    const onMessagesScroll = () => {
      updateJumpVisibility()
      scheduleMarkRead()
    }

    const onAttentionChange = () => {
      scheduleMarkRead()
    }

    const loadInitial = async () => {
      loadingInitial.value = true
      loadError.value = ''
      resetJumpCycle()
      historyExhausted = false
      try {
        const [listResponse, headResponse] = await Promise.all([
          GroupsService.listMessages(props.groupId, { limit: HISTORY_PAGE_LIMIT }),
          GroupsService.getChatHead(props.groupId),
        ])
        if (disposed) return
        messages.value = listResponse.data
        knownHeadId.value = headResponse.data.lastMessageId
        historyExhausted = listResponse.data.length < HISTORY_PAGE_LIMIT
        applyReadState(headResponse.data)
        await scrollToBottom()
      } catch {
        if (!disposed) {
          loadError.value = 'No se pudo cargar el chat'
        }
      } finally {
        if (!disposed) {
          loadingInitial.value = false
        }
      }
      if (!disposed && !loadError.value) {
        await nextTick()
        updateJumpVisibility()
        scheduleMarkRead()
      }
    }

    const pollHead = async () => {
      if (disposed || document.hidden) return
      try {
        const response = await GroupsService.getChatHead(props.groupId)
        if (disposed) return
        const nextId = response.data.lastMessageId
        applyReadState(response.data)
        if (nextId === knownHeadId.value) {
          updateJumpVisibility()
          scheduleMarkRead()
          return
        }

        const stickToBottom = isNearBottom()
        const afterId = messages.value.length > 0
          ? messages.value[messages.value.length - 1].messageId
          : undefined
        const listResponse = await GroupsService.listMessages(
          props.groupId,
          afterId ? { afterId } : {},
        )
        if (disposed) return
        mergeMessages(listResponse.data)
        knownHeadId.value = nextId
        if (stickToBottom) {
          await scrollToBottom()
        }
        updateJumpVisibility()
        scheduleMarkRead()
      } catch {
        // Keep polling quietly; transient network errors should not spam the UI.
      }
    }

    const send = async () => {
      const content = draft.value.trim()
      if (!content || sending.value || rateLimitSecondsLeft.value > 0) return

      sending.value = true
      sendError.value = ''
      try {
        const response = await GroupsService.sendMessage(props.groupId, content)
        draft.value = ''
        mergeMessages([response.data])
        if (!knownHeadId.value || response.data.messageId > knownHeadId.value) {
          knownHeadId.value = response.data.messageId
        }
        await scrollToBottom()
        void markReadNow()
      } catch (error: any) {
        const errorCode =
          error?.response?.data?.errorCode || error?.response?.data?.data?.errorCode
        if (error?.response?.status === 429 || errorCode === GROUP_CHAT_RATE_LIMIT_ERROR_CODE) {
          const retryAfter = extractRetryAfterSeconds(error) || 60
          startRateLimitCountdown(retryAfter)
          return
        }
        sendError.value = 'No se pudo enviar el mensaje'
      } finally {
        sending.value = false
      }
    }

    const startPolling = () => {
      if (pollTimer) return
      pollTimer = setInterval(() => {
        void pollHead()
      }, POLL_INTERVAL_MS)
    }

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }

    watch(
      () => props.groupId,
      async () => {
        messages.value = []
        knownHeadId.value = null
        lastReadMessageId.value = null
        unreadCount.value = 0
        historyExhausted = false
        resetJumpCycle()
        clearMarkTimer()
        await loadInitial()
      },
    )

    onMounted(async () => {
      window.addEventListener('focus', onAttentionChange)
      document.addEventListener('visibilitychange', onAttentionChange)
      await loadInitial()
      startPolling()
    })

    onBeforeUnmount(() => {
      disposed = true
      clearJumpTimer()
      clearMarkTimer()
      stopPolling()
      clearRateLimitTimer()
      window.removeEventListener('focus', onAttentionChange)
      document.removeEventListener('visibilitychange', onAttentionChange)
    })

    return {
      messages,
      draft,
      loadingInitial,
      sending,
      loadError,
      sendError,
      rateLimitSecondsLeft,
      messagesEl,
      showJumpToUnread,
      jumpLoading,
      currentUserId,
      canSend,
      formatContent,
      formatTime,
      send,
      onMessagesScroll,
      jumpToFirstUnread,
    }
  },
})
</script>

<style scoped>
.community-group-chat {
  display: flex;
  flex-direction: column;
  min-height: 360px;
  max-height: 640px;
  overflow: hidden;
}

.community-group-chat--card {
  z-index: 0;
  height: min(70vh, 640px);
  max-height: min(70vh, 640px);
}

.community-group-chat--embedded {
  min-height: 420px;
  max-height: 70vh;
  background: transparent;
}

.community-group-chat--overlay {
  flex: 1;
  min-height: 0;
  max-height: none;
  height: 100%;
  background: transparent;
  box-shadow: none;
}

.community-group-chat__header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.community-group-chat__thread {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.community-group-chat__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.community-group-chat__jump {
  position: absolute;
  top: 12px;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
}

.community-group-chat__jump :deep(.v-btn) {
  text-transform: none;
  letter-spacing: normal;
}

.community-group-chat__empty {
  margin: auto;
  text-align: center;
  padding: 24px 12px;
}

.community-group-chat__message {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  max-width: 92%;
}

.community-group-chat__message--own {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.community-group-chat__bubble {
  min-width: 0;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.community-group-chat__message--own .community-group-chat__bubble {
  background: rgba(var(--v-theme-primary), 0.12);
}

.community-group-chat__meta {
  display: flex;
  gap: 8px;
  align-items: baseline;
  margin-bottom: 2px;
}

.community-group-chat__alias {
  font-size: 0.75rem;
  font-weight: 600;
}

.community-group-chat__time {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.community-group-chat__content {
  font-size: 0.9rem;
  line-height: 1.35;
  word-break: break-word;
}

.community-group-chat__content :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.community-group-chat__composer {
  padding: 12px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.community-group-chat__composer-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
